/**
 * Page 8 — Data & method. The audit trail. Keep it.
 *
 * Source counts here are read from the LIVE bundle, not typed in, so the page cannot
 * drift from the data it describes.
 */
import { COLLECTION_LABEL } from '@/measures/collection'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useLoadedDataset } from '@/data/DatasetProvider'
import type { MethodAnchor } from '@/measures/definitions'
import * as m from '@/measures/core'
import { REASONS, NEW_HIRE_MONTHS } from '@/measures/noClaimReason'
import { int, pct, money0 } from '@/format'

const CAVEATS = [
  {
    title: 'Unattributed labor is month-grain',
    body: 'Labor lands on no claim a month at a time: a person’s labor in a paid month they billed no claims is unattributed, all of it, and in a month they billed any it all sits on claims. So a person can carry attributed claims and unattributed labor in the same period — {mixedPeople} people here do, for {mixedLabor} between them. Payroll without claims lists them month by month. The earlier measure summed the labor of people with no claims at all, which missed that labor.',
  },
  {
    title: 'Four departments are left out',
    body: 'Part B, MCS, Opex and Hospice carry payroll and no claims at all, so they are left out of every figure, as they are in the Power BI report. Their pay is not in the org’s cost per claim.',
  },
  {
    title: 'Two claim denominators',
    body: 'Billed and resolved differ by roughly {gap} at org level. "Cost per claim" always means billed; "cost per resolved claim" always means resolved. Blending them is the single most likely error in any port of this report.',
  },
  {
    title: '{pendingMonth}: payroll still loading',
    // Only shown while the build holds an ended month open for its payroll.
    whenPending: true,
    body: 'The month has ended and its claims are all in, but its payroll has not all loaded: some payroll sources send it weeks later. Until it does, a figure that keeps every claim (Facilities, the Overview’s breakdown bars) reads a fraction of the labor over a full month of claims, and person figures lose everyone not yet paid. So the build holds the month open, like the running month, until the people paid and the payroll are back within 15% of the three months before it.',
  },
  {
    title: '{openMonth} is not closed',
    // Only shown while the data marks a month as open; the title names it.
    whenOpen: true,
    body: 'The month is still running, and its ratios are not an early read: pay and billed claims land on different days, so a department can show one pay period against a handful of claims, or the reverse. Every figure leaves it out unless you pick it in the month range ("Include … (in progress)"); picked, it is shaded on every time series and marked with an asterisk in every grid.',
  },
  {
    title: 'What the month range changes',
    body: 'Every page’s figures are rebuilt from the months in the range: billed claims, claim labor, unattributed labor, cost per claim, resolved claims, the touch shares, open claims worked, the peer averages and the score. Those are exact. Four things the bundle does not split by month are handled differently. Collection rate is not read over the range’s own months at all but over its lagged window of bill months (see Collection rate below), added up and divided once, which is exact. Facilities handled is the busiest month in the range, a floor. Touches per claim is blank. The state, payer and facility breakdown is split in proportion to each person’s labor and claims in the range. Payroll stays whole-period, and the open backlog is always a snapshot at export. The figures on this page are always whole-period.',
  },
]

/** How this app's figures differ from the Power BI report. See References/MEASURE-RULES.md §0.1, §2.1, §10. */
const CHANGED = [
  'Facilities assigned leaves out UNKNOWN facilities. Four Power BI reports count them, so this figure and average claims per facility won’t tie.',
  'Every claim count is on one stated basis, billed or resolved. Power BI’s default counts claims by entry date, which is neither.',
  'People are ranked only against others with the same job title, and a group of fewer than five gets no rank. Power BI has no peer ranking.',
]

/*
 * The claim type, service type and status filters are exact since the 2026-10-07 bundle
 * (review #16): each person's figures are split by claim and the filters add up the
 * cells they pick (data/claimCells.ts). What they still cannot split is listed here.
 */
const MODELLED = [
  {
    figure: 'Weighted claims under a claim type / service type / status filter',
    why: 'The claim rules’ adjustment arrives per person and month, not per claim type, so under those filters it is split by each month’s share of billed claims. Every other figure under them is exact.',
  },
  {
    figure: 'Overview bars by state, payer type or facility under a claim filter',
    why: 'The breakdown is not split by claim type, so each person’s rows take their filtered share. The bars still add up to the tiles.',
  },
  {
    figure: 'The tiles, people, teams, scorecard and trend under a Facility group filter',
    why: 'A person’s figures are not split by facility. Their claims and claim labor are their share in the groups, off the facility breakdown — the Overview’s bars add up to exactly that — and every other figure, each month included, is scaled by the same share. Labor on no claim has no facility, so it drops out. The Overview’s bars, Facilities, Claim detail, Touch detail and Worklist assignments carry the facility and filter their rows exactly.',
  },
]

/**
 * What each number on this site means. The definitions live in code, in
 * src/measures/, and this is the same set written out — the point of the page is
 * that somebody can check a figure they have been shown without reading TypeScript.
 */
/**
 * `id` is the anchor a tooltip's "How it's calculated" link lands on
 * (measures/definitions.ts), so it is typed against that list.
 */
const DEFINITIONS: { term: string; body: string; id?: MethodAnchor }[] = [
  {
    term: 'Cost per claim',
    id: 'cost-per-claim',
    body: 'Payroll labor divided by BILLED claims, for whatever set of people is in scope. Everyone’s pay is in it, including people who billed no claims, so it is what a claim really costs; there is no separate “attributed” rate (dropped 2026-09-23). It is a weighted ratio — total over total — never the average of each person’s own rate, which would let a three-claim employee move a department.',
  },
  {
    term: 'Payroll labor',
    id: 'payroll-labor',
    body: 'Each person’s benefit-loaded pay, month by month, from the scorecard’s own payroll. A person counts only in months they were paid. It is the numerator of cost per claim and the figure behind every “payroll labor” on this site except one person’s own (below). It follows the month range.',
  },
  {
    term: 'A person’s payroll labor and hours',
    id: 'person-pay',
    body: 'On the Employee view and a person’s detail, payroll labor is their pay straight from payroll: bonus included, benefit load left out, so it is lower than the benefit-loaded figure cost per claim divides. Total worked is every paid hour; overtime and PTO are shown beside it but are already inside it, so the three never add up. Only Paylocity payroll records overtime and PTO — staff on the offshore, Israel and Montreal payrolls show zero. None of it is ranked or compared with peers. It follows the month range.',
  },
  {
    term: 'Billed claims',
    id: 'billed-claims',
    body: 'Claims by bill date, allocated to the person who worked them — the denominator of cost per claim. Per month, it is billed claims ÷ the months in the range. Not the same as resolved claims, which are counted by resolve date.',
  },
  {
    term: 'Unattributed claim labor',
    id: 'unattributed-labor',
    body: 'Payroll labor that landed on no claim: a person’s labor in the paid months they billed no claims. Read it as missing attribution, not as idle staff: someone can carry both attributed claims and unattributed labor, in different months. It stays in cost per claim — the labor was paid to RCM staff, so the org’s cost per claim carries it whether or not a claim did. On the Overview’s breakdown it is its own bar, “No claim (unattributed)”, in every dimension except department, with no rate of its own: it has no facility, state or payer, and dividing it by anybody’s claims would invent one. Its “Labor rows” list it by person and month. “Unknown” is now only real claims whose facility did not resolve.',
  },
  {
    term: 'Why a paid month has no claims',
    id: 'no-claim-why',
    body: `Payroll without claims gives every claimless month one reason: the first of these that applies, so each dollar has exactly one and the reasons add up to the page total. ${REASONS.map(
      (r, i) =>
        `${i + 1}. ${r.label} — ${r.explain} From ${r.source}${r.basis === 'heuristic' ? ', a job-title guess' : r.basis === 'mixed' ? '; the title part is a keyword guess' : ''}.`,
    ).join(' ')} A person’s own reason is the one carrying most of their dollars; a month with another reason is marked on its cell. New hire counts ${NEW_HIRE_MONTHS} months from the hire date, which the bundle carries from 9 October 2026 and older bundles back out of tenure, to within a few days. These are explanations, not measures: nothing here changes a dollar.`,
  },
  {
    term: COLLECTION_LABEL,
    id: 'collection-rate',
    body: 'Charge on resolved claims divided by total charge, for claims billed over a window that ends three months before the range and runs six months back from there: for a range from Start to End, the bills of Start − 8 months through End − 3 months. One month, say January 2026, reads May–October 2025; a longer range adds its extra months to the window. The lag gives bills time to resolve, so the latest months do not always read lowest. It is the semantic model’s Collected Percentage (Back 3 months, Rolling 6 months), anchored on the range rather than on today. The six months are pooled — the charge is added up and divided once, never an average of six monthly rates — and so are people (a team, a department, the whole org). In a month-by-month grid each month is its own six-month window, and the row’s total is the range’s window, not an average of the months. Only bills from months the person was paid count. “Collected” means the claim reached Resolved: no payment amount in the data can yet be tied to a claim, so this is not cash received (decided 5 October 2026).',
  },
  {
    term: 'Costs above peer average',
    id: 'costs-above-peer',
    body: '(A person’s cost per claim − their peer-group average cost per claim) × their billed claims. The peer group is the scorecard’s: same department and job title, on the same shore. Positive means dearer than the group; negative means cheaper.',
  },
  {
    term: 'Claims billed vs resolved %',
    id: 'billed-vs-resolved',
    body: 'Resolved claims divided by billed claims — called “closed claim %” until 2026-09-22, when the scorecard’s own name for it was adopted. It is NOT capped at 100%. Resolution lags billing, so anyone clearing a backlog legitimately exceeds it, and capping it hides exactly the people it should surface. Below 50% it is the one line that takes points away.',
  },
  {
    term: 'Open claims worked in month %',
    id: 'open-worked',
    body: 'Of the claims open to a person in a month, the share they touched that month. Only the person’s own touches count. Pooled across the period rather than averaged, so a month holding 900 open claims outweighs one holding 4. Not the same as the 30- and 14-day figures below, which count anyone’s touch and look back from one date.',
  },
  {
    term: 'Open claims by when last worked',
    id: 'open-recency',
    body: 'Each claim open at the latest worklist snapshot (system worklists left out) is matched to its most recent touch by anyone, on or before the snapshot date. Days are counted back from that date: worked in the last 30 days, last worked over 30 days ago, or never worked — no touch at all since touch records begin. The three add up to the open pile, and every page shows the pile this way — each bucket as a count and a share of the pile, in this order — and no other recency figure. The snapshot date is the month end, except for the month in progress, where it is the day the open claims were last loaded. Data built before 1 October 2026 has no last touch; there the Backlog page falls back to the pile less what its owner touched in the snapshot’s month, which is not the same as never worked.',
  },
  {
    term: 'Cost per resolved claim',
    body: 'A different measure on a different denominator, not a variant of cost per claim. The two bases differ by roughly {gap} at org level. Any figure that blends them is wrong.',
  },
  {
    term: 'Touch shares',
    id: 'touch-shares',
    body: 'Of the claims a person resolved in the month — whenever they were billed — the share closed with no touch, exactly one touch, or two or more. A high zero-touch share means claims resolving without manual work. Touches per touched claim is touches ÷ claims touched at least once, over the same claims. Claim detail opens them as “Resolved in” the month, across every bill month. Before 12 October 2026 these were counted by the month the claims were billed.',
  },
  {
    term: 'Facilities handled',
    id: 'facilities',
    body: 'Distinct facilities a person billed claims for. Facilities that resolve to UNKNOWN are not counted — the model’s own rule, which four Power BI reports override (see below). For a team, it is averaged per person carrying claims, because the data holds each person’s count rather than which facilities they were.',
  },
  {
    term: 'Team figures',
    id: 'team-figures',
    body: 'A team is the supervisor’s whole reporting tree — everyone below them at every level — or their direct reports only, as chosen on My metrics’ team view. Team cost per claim covers everyone on it, people with no claims included; the other rates cover the people carrying claims. The supervisor’s own figures are in only when management is included. Teams are ranked against supervisors with the same job title — a Medicare Supervisor against Medicare Supervisors only. Hover a rank to see who it is against.',
  },
  {
    term: 'Management',
    id: 'management',
    body: 'The payroll management flag — supervisors, managers, directors, VPs; an Account Manager is not caught by the word in the title. The “Include management” switch at the top of each page sets whether they are in the figures. The Overview includes them; My metrics, Supervisors and Scorecard leave them out by default, so a supervisor covering a few claims does not read as an outlier or skew their team. Opening a tab puts its default back.',
  },
  {
    term: 'Filters that follow you',
    id: 'filters',
    body: 'Every filter in the bar at the top follows you from page to page, and Back puts each page’s filters back as they were. One exception: on Supervisors and the Scorecard, Employee is set per page. An employee picked on another page — My metrics, Employees — is not applied there: the scope line shows it as “not applied”, with Apply here to use it and × to clear it, and the pages you go on to still have it. Pick an employee on Supervisors or the Scorecard itself and it applies there as usual. The controls a page draws itself, such as the Overview’s slicer or Claim detail’s dropdowns, belong to that page only. Clear filters clears both.',
  },
  {
    term: 'Weighted claims',
    id: 'weighted-claims',
    body: 'The “Weighted claims” switch at the top of each page counts each billed claim by the claim rules, which rule editors keep on the Rules page. A coinsurance payer’s Co-Insurance claims count at its weight — 0.5 is half a claim — or not at all for the biller (HMO or Medicare) it is excluded from; the same payer billed as the primary counts as usual. A hard payer’s claims count at its weight (1.5 is one and a half; every payer starts at 1), and the two multiply. Cost per claim, claims per month, scores and ranks then use the weighted count. Claims billed vs resolved % never does: resolved claims are not weighted. Every page opens with it off except the Scorecard, which opens with it on. Claim detail shows each claim’s weight and why. The breakdowns by state, payer type and facility follow it too. Each person’s claims in each of them are weighted the same way, so the bars still add up to the cards.',
  },
  {
    term: 'Peer group',
    id: 'peer-group',
    body: 'Scored against: department plus job title — plus on-shore for cost comparisons, since onshore and offshore cost bases are not comparable. Ranked within: everyone active in the same job title who was on the books in the period, whether or not they have a figure. Anyone with nothing to rank on sits at the bottom as "no data", not as a real last place, and averages are taken over the people with a figure. Fewer than five with a figure produces no rank at all; the app prints "no cohort" rather than ranking someone against two people. Hover any rank for its position, the size of the pool and who is in it.',
  },
  {
    term: 'Score',
    id: 'score',
    body: 'Points against the peer average: cost per claim (+3), claim count (+2), collection rate (+2), claims billed vs resolved % (+2), 0% touch share (+0.5), open claims worked in month (+1), facilities handled (+0.5), and −3 when claims billed vs resolved % is under 50%. The range is −3 to +11, in half points. A measure with no figure scores 0. Below Minimum Billing Volume: a full month (not a hire, leaving or short-hours month) billed under 90% of the average of the bottom 25% of the job title (people in full months only) scores 0, flagged, with the score it would have had shown; over a range, the billed claims of the person’s full months against the sum of their minimums. Those scored 0 are left out of everyone else’s peer averages. 0% touch share leaves out, numerator and base, the no-touch claims resolved past their payer type’s age limit (Rules page).',
  },
]

/**
 * How a claim reaches a person, in plain words (reviews #394, #405, #406). The rule of
 * record is References/MEASURE-RULES.md §12.9; the model decides all of it and the app
 * copies what it decided. Claim detail's ⓘ shows these steps for one claim.
 */
const ATTRIBUTION = [
  {
    title: 'A billed claim goes to the person on its predicted worklist',
    body: 'Medevolve sorts claims onto worklists by facility, payer type, service type and plan. The model looks at how every worklist was set up at the end of the claim’s bill month and picks the one that covers the claim: its predicted worklist. Where two cover it — say two “HMO Inhouse Primary” lists that both include the same facility — it takes the one with the higher priority. The claim is credited to the person assigned to that worklist at that month end. When a worklist has more than one person on it, the model takes the first by email address.',
  },
  {
    title: 'It is a prediction, not proof of who worked it',
    body: 'Most billed claims are never touched before they bill, so for most of them the prediction is the only evidence of whose they are. Who touched a claim shows on Touch detail; it does not move the claim. When the setup is wrong, one person can be credited with a whole list.',
  },
  {
    title: 'When there is no prediction, or the person has left',
    body: 'No predicted worklist: the claim goes to the owner of the first real worklist (not a system list or a holding queue) it was actually put on. The person had left by the bill date: it goes to the next person who held the claim on a real worklist, or stays with them if nobody has held it since. From 12 October 2026 a co-insurance claim follows its primary claim (same patient, facility and service month) to that claim’s owner; when the primary has no assignee, or there is no primary that month, it keeps its own prediction. Claim detail’s ⓘ names the rule the model recorded for each claim, and links the primary.',
  },
  {
    title: 'Expected at billing, beside who actually worked it',
    body: 'Claim detail’s ⓘ also shows who was expected to work the claim when it billed, from the period table: the predicted worklist and person for the claim’s facility, payer type, service type and plan, the dates that setup ran, and how many worklists it was chosen from. A flag marks a worklist that covers the facility only through an old setting: Worklist assignments shows the facility taken off it before the bill month ended. Below it is the first real worklist and person the claim actually sat on after billing, and whether that is the same as expected. They often differ, and that is usually a lag, not an error. Most claims wait about a month on a system list before a person gets them, and when a facility has just been reassigned, about one claim in five stays with the previous owner. The credit itself does not change: it is still the model’s rule above. A claim the period table has no single row for (mostly co-insurance, where the plan is the primary claim’s) shows none.',
  },
  {
    title: 'An open claim counts in the pile of whoever holds it at each month end',
    body: 'For the backlog the app does not use the prediction. At every month end a claim is still open, it counts in the open pile of the person it was actually assigned to in Medevolve on that day, on the worklist it sat on. A claim on a system list that month is in nobody’s pile. So a claim can be credited to one person when billed and sit in another’s pile while open; Claim detail’s ⓘ lists it month by month.',
  },
  {
    title: 'Worklist assignments',
    body: 'Who was assigned to each worklist, for which facilities and on which days, read from Medevolve’s configuration history from 1 March 2026. Claim detail’s Predicted worklist opens it at the claim’s worklist and bill month. Priorities change almost every week, so the page shows each worklist’s priority on the days in question, from the priority history; it starts 7 July 2026, and before that the page shows today’s priority, marked “today”.',
  },
]

export function DataMethod() {
  const dataset = useLoadedDataset()

  // Arriving from a tooltip's "How it's calculated": bring that entry into view and
  // mark it, since a definition list gives the eye nothing else to land on.
  const { hash } = useLocation()
  const [target, setTarget] = useState('')
  useEffect(() => {
    const id = hash.slice(1)
    setTarget(id)
    if (id) document.getElementById(id)?.scrollIntoView({ block: 'center' })
  }, [hash])

  // How far resolved runs below billed, org-wide, filled into the copy that cites it.
  const billed = m.billedClaims(dataset.employees)
  const gap = billed > 0 ? pct(1 - m.resolvedClaims(dataset.touchMonths) / billed, 0) : '—'
  // People carrying attributed claims AND labor that landed on no claim.
  const mixed = new Map<string, number>()
  for (const r of dataset.employees) {
    if (r.claims > 0 && r.unattributedLabor > 0) {
      mixed.set(r.employee, (mixed.get(r.employee) ?? 0) + r.unattributedLabor)
    }
  }
  // The month(s) the data marks as not yet closed, by name: "2026-08" -> "August".
  // The running month and a month held for its payroll get a caveat each.
  const monthName = (ym: string) => new Date(`${ym}-01T00:00:00`).toLocaleDateString('en-US', { month: 'long' })
  const pending = dataset.payrollPending ?? []
  const openMonth = dataset.openMonths.filter((m) => !pending.includes(m)).map(monthName).join(' and ')
  const pendingMonth = pending.map(monthName).join(' and ')
  const vars: Record<string, string> = {
    gap,
    openMonth,
    pendingMonth,
    mixedPeople: int(mixed.size),
    mixedLabor: money0([...mixed.values()].reduce((a, v) => a + v, 0)),
  }
  const fill = (text: string) => text.replace(/\{(\w+)\}/g, (all, k: string) => vars[k] ?? all)

  const sources = [
    { name: 'Employee', grain: 'One row per employee', rows: dataset.employees.length, note: 'The authoritative fact table. Claims here are BILLED claims.' },
    { name: 'TouchMonth', grain: 'Month × employee', rows: dataset.touchMonths.length, note: 'Billed and resolved claims, touch mix and labor by month. Its billed total matches the Employee table.' },
    { name: 'WorklistBalance', grain: 'One row per employee', rows: dataset.worklist.length, note: 'Only people holding a live, non-system worklist. Read the backlog as a floor.' },
    { name: 'DimensionCrosstab', grain: 'Employee × dept × state × payer × facility', rows: dataset.crosstab.rows.length, note: 'Dictionary-encoded. Drives the facility and payer breakdowns.' },
    { name: 'ClaimComposition', grain: 'One row per employee', rows: dataset.composition.length, note: 'Per-person claim-type, service-type and status shares — claim counts AND labor dollars, separately. Powers the claim-grain filters.' },
  ]

  return (
    <>
      <h1 className="page-title">What this is built from</h1>
      <p className="body-note" style={{ marginTop: 6 }}>
        Five tables from the FCC Web App lakehouse. No figure is estimated unless it
        carries an <span className="modeled">M</span> badge, and those are labeled where
        they appear.
      </p>
      {dataset.claimVolumeOutliers && (
        <p className="body-note" style={{ marginTop: 6 }}>
          Claim-volume check:{' '}
          {dataset.claimVolumeOutliers.people === 0
            ? 'nobody you can see bills far above their job title.'
            : `${int(dataset.claimVolumeOutliers.personMonths)} person-months on ` +
              `${int(dataset.claimVolumeOutliers.people)} ` +
              `${dataset.claimVolumeOutliers.people === 1 ? 'person' : 'people'} bill more than ` +
              `${dataset.claimVolumeOutliers.factor}× their job title's median that month ` +
              `(and at least ${int(dataset.claimVolumeOutliers.margin)} over it). Usually the ` +
              'predicted-worklist matcher handing one person a whole list, not the work they did.'}
        </p>
      )}

      <div className="mgrid">
        <div className="card">
          <h3 className="card-heading">Sources</h3>
          <div style={{ marginTop: 10 }}>
            {sources.map((s) => (
              <div key={s.name} className="msrc">
                <div className="msrc-file">{s.name}</div>
                <div className="msrc-title">{s.grain}</div>
                <div className="msrc-desc">
                  {int(s.rows)} rows. {s.note}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="card">
          <h3 className="card-heading">Definitions</h3>
          <dl className="mdefs">
            {DEFINITIONS.map((d) => (
              <div key={d.term} style={{ display: 'contents' }}>
                <dt id={d.id} className={d.id && d.id === target ? 'is-target' : undefined}>
                  {d.term}
                </dt>
                <dd className={d.id && d.id === target ? 'is-target' : undefined}>{fill(d.body)}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      <section className="section" id="attribution">
        <h2 className={`section-heading${target === 'attribution' ? ' is-target' : ''}`}>How a claim is attributed</h2>
        <div style={{ marginTop: 14 }}>
          {ATTRIBUTION.map((c) => (
            <div key={c.title} className="caveat">
              <h4>{c.title}</h4>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section">
        <h2 className="section-heading">Caveats a developer needs before wiring this to live data</h2>
        <div style={{ marginTop: 14 }}>
          {CAVEATS.filter((c) => (!('whenOpen' in c) || openMonth !== '') && (!('whenPending' in c) || pendingMonth !== '')).map((c) => (
            <div key={c.title} className="caveat">
              <h4>{fill(c.title)}</h4>
              <p>{fill(c.body)}</p>
            </div>
          ))}
          {MODELLED.map((x) => (
            <div key={x.figure} className="caveat">
              <h4>
                {x.figure} <span className="modeled">M</span>
              </h4>
              <p>{x.why}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section">
        <h2 className="section-heading">What changed from the Power BI report</h2>
        <div style={{ marginTop: 14 }}>
          {CHANGED.map((c) => (
            <div key={c} className="mdelta">{c}</div>
          ))}
        </div>
      </section>
    </>
  )
}
